import { MainLayout } from '@/components/layout/MainLayout';
import { useMultitrack } from '@/components/multitrack/useMultitrack';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Volume2, VolumeX, Play, Pause, Headphones, MoreHorizontal, Download } from 'lucide-react';
import { useStemSession } from '@/hooks/useStemSession';
import { useEffect } from 'react';

const MultitrackTest = () => {
  const {
    sessions,
    activeSession,
    setActiveSession
  } = useStemSession();

  useEffect(() => {
    if (sessions?.length && !activeSession.id) {
      const completedSessions = sessions.filter(s => s.status === 'completed');
      if (completedSessions.length > 0) {
        const mostRecent = completedSessions.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
        setActiveSession({
          id: mostRecent.id,
          type: 'stem'
        });
      }
    }
  }, [sessions, activeSession.id, setActiveSession]);

  const currentSession = sessions?.find(s => s.id === activeSession.id);
  const stems = currentSession?.stems as {
    name: string;
    url: string;
    duration: number;
  }[] || [];

  console.log('MultitrackTest - Current Session:', currentSession);
  console.log('MultitrackTest - Available Stems:', stems);

  const {
    containerRef,
    isPlaying,
    tracks,
    masterVolume,
    loadProgress,
    togglePlayPause,
    setTrackVolume,
    setTrackMute,
    setTrackSolo,
    setGlobalVolume
  } = useMultitrack(stems, currentSession?.original_audio_url || '');

  if (!sessions?.length) {
    return <MainLayout>
      <div className="p-8">
        <h1 className="text-2xl font-bold text-white mb-4">Multitrack Player Test</h1>
        <p className="text-muted-foreground">
          Please process a track in the AI Stem Splitter first to test the multitrack player.
        </p>
      </div>
    </MainLayout>;
  }

  if (!currentSession) {
    return <MainLayout>
      <div className="p-8">
        <h1 className="text-2xl font-bold text-white mb-4">Multitrack Player Test</h1>
        <div className="space-y-4">
          <p className="text-muted-foreground">Select a processed track to test:</p>
          <div className="space-y-2">
            {sessions.filter(s => s.status === 'completed').map(session => (
              <Button 
                key={session.id} 
                variant="outline" 
                onClick={() => setActiveSession({
                  id: session.id,
                  type: 'stem'
                })}
              >
                {session.original_filename}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </MainLayout>;
  }

  const showDownloadAll = currentSession?.status === 'completed' && currentSession?.stems && (currentSession.stems as any[]).length > 0;

  const handleDownloadAll = async () => {
    // Placeholder for download all logic
  };

  const handleDawClick = () => {
    // Placeholder for DAW click logic
  };

  return (
    <MainLayout>
      <div className="p-4 md:p-8 space-y-8 w-full">
        <div className="max-w-full">
          <h1 className="text-2xl font-bold text-white truncate">Multitrack Player Test</h1>
        </div>
        
        <div className="flex flex-col space-y-4">
          <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:items-center md:justify-between w-full">
            <div className="flex items-center gap-6">
              <Button variant="outline" size="icon" onClick={togglePlayPause} className="h-12 w-12">
                {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
              </Button>

              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-muted-foreground">Master</span>
                <div className="flex items-center gap-2 w-32 md:w-48">
                  <VolumeX className="h-4 w-4" />
                  <Slider 
                    value={[masterVolume * 100]} 
                    onValueChange={value => setGlobalVolume(value[0] / 100)} 
                    max={100} 
                    step={1} 
                  />
                  <Volume2 className="h-4 w-4" />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="icon" 
                onClick={() => setActiveSession({
                  id: currentSession.id,
                  type: 'stem'
                })}
                className="h-10 w-10"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>

              {showDownloadAll && (
                <Button onClick={handleDownloadAll} variant="outline" className="h-10 px-4">
                  <Download className="w-4 h-4 mr-2" />
                  <span>Download All</span>
                </Button>
              )}

              <Button variant="default" className="bg-[#3B82F6] hover:bg-[#60A5FA] text-white h-10 px-4" onClick={handleDawClick}>
                <img src="/lovable-uploads/1f60214c-efc7-49df-9a80-67c09b9772bf.png" alt="Sesh Logo" className="w-4 h-4 mr-2" />
                Open in Sesh
              </Button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="flex min-w-[800px]">
            <div className="w-[280px] md:w-[320px] flex-shrink-0 flex flex-col gap-[2px]">
              {tracks.map(track => (
                <div 
                  key={track.id} 
                  className="flex flex-col md:flex-row md:items-center gap-2 px-2 bg-card"
                >
                  <div className="flex items-center justify-between md:justify-start md:w-auto w-full py-2 md:py-0">
                    <span className="text-sm font-medium truncate w-20">{track.name}</span>
                    
                    <div className="flex md:hidden items-center gap-1">
                      <Button 
                        variant={track.isMuted ? "destructive" : "ghost"} 
                        size="icon" 
                        onClick={() => setTrackMute(track.id)} 
                        className="h-8 w-8"
                      >
                        <VolumeX className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant={track.isSoloed ? "default" : "ghost"} 
                        size="icon" 
                        onClick={() => setTrackSolo(track.id)} 
                        className="h-8 w-8"
                      >
                        <Headphones className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 pb-2 md:pb-0">
                    <div className="flex-1 md:w-32">
                      <Slider 
                        value={[track.volume * 100]} 
                        onValueChange={value => setTrackVolume(track.id, value[0] / 100)} 
                        max={100} 
                        step={1} 
                      />
                    </div>

                    <div className="hidden md:flex items-center gap-1">
                      <Button 
                        variant={track.isMuted ? "destructive" : "ghost"} 
                        size="icon" 
                        onClick={() => setTrackMute(track.id)} 
                        className="h-8 w-8"
                      >
                        <VolumeX className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant={track.isSoloed ? "default" : "ghost"} 
                        size="icon" 
                        onClick={() => setTrackSolo(track.id)} 
                        className="h-8 w-8"
                      >
                        <Headphones className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex-1 relative min-w-0">
              <div 
                id="loading-bars-container"
                data-testid="loading-bars-container"
                className="absolute inset-0 z-10 pointer-events-none"
                style={{
                  height: `${tracks.length * (window.innerWidth >= 768 ? 48 : 76)}px`
                }}
              >
                {tracks.map((track, index) => (
                  <div
                    key={`loading-${track.id}`}
                    className="absolute w-full"
                    style={{
                      top: `${index * (window.innerWidth >= 768 ? 48 : 76)}px`,
                      height: window.innerWidth >= 768 ? '48px' : '76px',
                    }}
                  >
                    <div className="relative h-full bg-black/20">
                      <div 
                        className="absolute inset-0 transition-all duration-300 ease-out"
                        style={{
                          width: `${loadProgress[track.id] || 0}%`,
                          backgroundColor: track.color,
                          opacity: 0.5
                        }}
                      >
                        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div 
                ref={containerRef} 
                className="relative z-20 bg-black/20 rounded-lg overflow-hidden" 
                style={{
                  height: `${tracks.length * (window.innerWidth >= 768 ? 48 : 76)}px`
                }} 
              />
            </div>
          </div>
        </div>

        <div className="text-sm text-muted-foreground max-w-full">
          <p className="truncate">Currently playing: {currentSession?.original_filename}</p>
          <div className="mt-2">
            <p>Loading Progress:</p>
            {tracks.map(track => (
              <p key={`progress-${track.id}`} className="truncate">
                {track.name}: {loadProgress[track.id]?.toFixed(1) || 0}%
              </p>
            ))}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default MultitrackTest;
